import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { NecessidadeItem } from '@triade/contracts';
import { useNecessidade } from '@/api/plano';
import { ErroLista } from '@/components/cargos';
import { formatarContagem, formatarData, formatarQuantidade } from '@/lib/format';
import { mesAtual, periodoDoMes } from './compras-busca';
import { UsaAlimenta } from './usa-alimenta';
import { BotaoRelatorios } from '@/relatorios';

const CAMPO = 'h-[34px] rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none';

/**
 * Necessidade de MP (/plano/necessidade): por semana (segunda ISO) e por MP, em toneladas, com o que ja esta
 * comprado/a receber e o saldo. Tudo vem pronto da API; a tela so monta o periodo e formata.
 */
export function NecessidadePage() {
  const [periodo, setPeriodo] = useState(() => periodoDoMes(mesAtual()));
  const [incluirPendente, setIncluirPendente] = useState(false);
  const invalido = periodo.de > periodo.ate;
  const necessidade = useNecessidade({ de: periodo.de, ate: periodo.ate, incluirPendente });

  return (
    <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-small text-ink-muted">
            Plano <span aria-hidden="true">/</span> Necessidade de MP
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Necessidade de MP</h1>
          <p className="text-small text-ink-secondary">Quanto de matéria-prima os pedidos exigem por semana, o que já está comprado e o que falta comprar (em toneladas)</p>
          <UsaAlimenta
            usa={[
              { rotulo: 'Carteira', to: '/plano/carteira' },
              { rotulo: 'Fichas técnicas', to: '/custos/fichas-tecnicas' },
            ]}
            alimenta={[{ rotulo: 'Compras de MP', to: '/plano/compras' }]}
          />
        </div>
        <BotaoRelatorios tela="plano-necessidade" params={{ de: periodo.de, ate: periodo.ate, incluirPendente: incluirPendente ? 'true' : undefined }} />
      </div>

      <section aria-label="Período da necessidade" className="flex flex-wrap items-end gap-3 rounded-2xl border border-border bg-card p-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="necessidade-de" className="text-caption font-medium text-ink-secondary">De</label>
          <input id="necessidade-de" type="date" className={CAMPO} value={periodo.de} onChange={(e) => setPeriodo((p) => ({ ...p, de: e.target.value }))} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="necessidade-ate" className="text-caption font-medium text-ink-secondary">Até</label>
          <input id="necessidade-ate" type="date" className={CAMPO} value={periodo.ate} onChange={(e) => setPeriodo((p) => ({ ...p, ate: e.target.value }))} />
        </div>
        <label className="flex items-center gap-2 pb-1.5 text-small text-ink-body">
          <input type="checkbox" checked={incluirPendente} onChange={(e) => setIncluirPendente(e.target.checked)} />
          Incluir pedidos pendentes
        </label>
      </section>

      {invalido ? (
        <p role="alert" className="px-5 py-10 text-center text-small text-danger-ink">A data inicial é depois da data final. Ajuste o período.</p>
      ) : necessidade.isError && !necessidade.data ? (
        <div className="p-4"><ErroLista erro={necessidade.error} onTentar={() => void necessidade.refetch()} titulo="Não foi possível calcular a necessidade" /></div>
      ) : !necessidade.data ? (
        <p role="status" className="px-5 py-10 text-center text-small text-ink-muted">Carregando necessidade…</p>
      ) : (
        <>
          {necessidade.data.semFicha.length > 0 && (
            <section aria-label="Pedidos sem ficha" className="flex flex-col gap-2 rounded-2xl border border-warning-ink/30 bg-warning-bg p-4 text-small text-warning-ink">
              <p className="font-semibold">
                {necessidade.data.semFicha.length === 1 ? '1 pedido ficou fora da conta' : `${formatarContagem(necessidade.data.semFicha.length)} pedidos ficaram fora da conta`}: sem ficha técnica ou
                sem custo na data.
              </p>
              <ul className="list-disc pl-5">
                {necessidade.data.semFicha.map((p) => (
                  <li key={p.pedidoId}>
                    Pedido nº {p.numero} ({p.itemCodigo}): {p.motivo}
                  </li>
                ))}
              </ul>
              <Link to="/custos/fichas-tecnicas" className="w-fit font-semibold underline underline-offset-2">
                Abrir fichas técnicas
              </Link>
            </section>
          )}

          <p className="text-caption text-ink-muted">
            Período de {formatarData(necessidade.data.de)} a {formatarData(necessidade.data.ate)}. {necessidade.data.incluiPendente ? 'Inclui pedidos pendentes.' : 'Pedidos pendentes ficam fora.'}
          </p>

          {necessidade.data.itens.length === 0 ? (
            <p role="status" className="rounded-2xl border border-border bg-card px-5 py-10 text-center text-small text-ink-muted">
              Nenhuma necessidade de MP no período: os pedidos não usam matéria-prima de ficha técnica, ou não há pedidos. Veja a{' '}
              <Link to="/plano/carteira" className="font-semibold underline underline-offset-2">
                Carteira
              </Link>
              .
            </p>
          ) : (
            <div className="flex flex-col gap-5">
              <div className="overflow-x-auto rounded-2xl border border-border bg-card">
                <table className="w-full min-w-[720px] border-collapse text-small" aria-label="Necessidade de MP por produto">
                  <thead>
                    <tr className="text-left text-caption text-ink-muted">
                      <th scope="col" className="px-4 py-2.5 font-medium">Código</th>
                      <th scope="col" className="px-3 py-2.5 font-medium">Matéria-prima</th>
                      <th scope="col" className="px-3 py-2.5 text-right font-medium">Necessidade (t)</th>
                      <th scope="col" className="px-3 py-2.5 text-right font-medium">Comprado / a receber (t)</th>
                      <th scope="col" className="px-3 py-2.5 text-right font-medium">A comprar (t)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {necessidade.data.itens.map((it) => (
                      <tr key={it.itemId} data-testid="linha-mp" className="border-t border-line-soft">
                        <td className="px-4 py-3 font-mono font-semibold">{it.codigo}</td>
                        <td className="px-3 py-3">{it.descricao}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{formatarQuantidade(it.necessidade)}</td>
                        <td className="px-3 py-3 text-right tabular-nums">{formatarQuantidade(it.compras)}</td>
                        <td className="px-3 py-3 text-right font-semibold tabular-nums">{formatarQuantidade(it.aComprar)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {necessidade.data.itens.map((it) => (
                <DetalheSemanas key={it.itemId} item={it} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DetalheSemanas({ item }: { item: NecessidadeItem }) {
  return (
    <details className="rounded-2xl border border-border bg-card" data-testid="detalhe-semanas">
      <summary className="cursor-pointer px-4 py-3 text-body font-semibold">
        Semana a semana: <span className="font-mono">{item.codigo}</span> {item.descricao}
      </summary>
      <div className="overflow-x-auto border-t border-border">
        <table className="w-full min-w-[640px] border-collapse text-small" aria-label={`Necessidade semanal de ${item.codigo}`}>
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Semana</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">Necessidade (t)</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">Comprado / a receber (t)</th>
              <th scope="col" className="px-3 py-2.5 text-right font-medium">A comprar (t)</th>
            </tr>
          </thead>
          <tbody>
            {item.semanas.map((s) => (
              <tr key={s.inicio} className="border-t border-line-soft">
                <th scope="row" className="px-4 py-3 text-left font-medium">
                  Semana {s.semana} <span className="text-ink-muted">· a partir de {formatarData(s.inicio)}</span>
                </th>
                <td className="px-3 py-3 text-right tabular-nums">{formatarQuantidade(s.necessidade)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{formatarQuantidade(s.compras)}</td>
                <td className="px-3 py-3 text-right font-semibold tabular-nums">{formatarQuantidade(s.aComprar)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
